import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Upload, Trash2, Search, Image as ImageIcon, Check } from 'lucide-react';
import { ImageService } from '../../services';

interface ImageSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  currentValue?: string;
  imageService: ImageService;
}

export const ImageSelector: React.FC<ImageSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentValue,
  imageService,
}) => {
  const [images, setImages] = useState<string[]>([]);
  const [filteredImages, setFilteredImages] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(currentValue || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).src = '/placeholder-image.svg';
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadImages();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const filtered = images.filter(image =>
      image.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredImages(filtered);
  }, [images, searchTerm]);

  const loadImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const imageList = await imageService.getImages();
      setImages(imageList);
    } catch (err) {
      setError('Error al cargar las imágenes');
      console.error('Error loading images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de archivo no permitido. Solo se permiten imágenes.');
      return;
    }

    // Validar tamaño (máximo 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('El archivo es demasiado grande. Máximo 10MB.');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const result = await imageService.uploadImage(file);
      setImages(prev => [...prev, result.filename]);
      setSelectedImage(result.url);
    } catch (err) {
      setError('Error al subir la imagen');
      console.error('Error uploading image:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (filename: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta imagen?')) {
      return;
    }

    try {
      await imageService.deleteImage(filename);
      setImages(prev => prev.filter(img => img !== filename));
      if (selectedImage === `/assets/images/${filename}`) {
        setSelectedImage(null);
      }
    } catch (err) {
      setError('Error al eliminar la imagen');
      console.error('Error deleting image:', err);
    }
  };

  const handleSelectImage = (filename: string) => {
    const imageUrl = `/assets/images/${filename}`;
    setSelectedImage(imageUrl);
  };

  const handleConfirmSelection = () => {
    if (selectedImage) {
      onSelect(selectedImage);
      onClose();
    }
  };

  // const formatFileSize = (bytes: number): string => {
  //   if (bytes === 0) return '0 Bytes';
  //   const k = 1024;
  //   const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  //   const i = Math.floor(Math.log(bytes) / Math.log(k));
  //   return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  // };

  if (!isOpen) return null;

  return (
    <div className="image-selector-overlay">
      <div className="image-selector-modal">
        <div className="image-selector-header">
          <h3>Selector de Imágenes</h3>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>

        <div className="image-selector-content">
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <div className="image-selector-toolbar">
            <div className="search-container">
              <Search size={16} />
              <input
                type="text"
                placeholder="Buscar imágenes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input"
              />
            </div>

            <div className="upload-container">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload size={16} />
                {uploading ? 'Subiendo...' : 'Subir Imagen'}
              </button>
            </div>
          </div>

          <div className="image-grid">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Cargando imágenes...</p>
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="empty-state">
                <ImageIcon size={48} />
                <p>No hay imágenes disponibles</p>
                <p>Sube una imagen para comenzar</p>
              </div>
            ) : (
              filteredImages.map((filename) => (
                <div
                  key={filename}
                  className={`image-item ${selectedImage === `/assets/images/${filename}` ? 'selected' : ''}`}
                  onClick={() => handleSelectImage(filename)}
                >
                  <div className="image-preview">
                    <img
                      src={imageService.getImageUrl(filename)}
                      alt={filename}
                      onError={handleImageError}
                    />
                    {selectedImage === `/assets/images/${filename}` && (
                      <div className="selected-indicator">
                        <Check size={20} />
                      </div>
                    )}
                  </div>
                  <div className="image-info">
                    <span className="image-name" title={filename}>
                      {filename}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(filename);
                      }}
                      title="Eliminar imagen"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="image-selector-footer">
          <div className="selected-info">
            {selectedImage && (
              <span>Imagen seleccionada: {selectedImage.split('/').pop()}</span>
            )}
          </div>
          <div className="actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirmSelection}
              disabled={!selectedImage}
            >
              Seleccionar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
